import React, { useEffect, useState } from "react";

import { View } from "react-native";
import {
  Text,
  Toast,
  Input,
  Switch,
  EditModal,
  InputLabel,
  GroupInfoBox,
  ConfirmAlert,
  AddItemButton,
  DeleteAlertComponent,
} from "components";

import css from "styles";
import { useFlag } from "hooks";
import {
  arrayGroupBy,
  changeArrayAt,
  isEqualArrays,
  priceFormatter,
} from "utils";
import { colors, patterns, sizes, strings } from "assets/constants";
import MultiSelectableItem from "screens/components/MultiSelectableItem";
import cartesian from "utils/cartesian";

export default function Variations(props: {
  items: ProductVariationT[];
  variantAttrs?: [];
  unitPrice?: string | number;
  onChange: (products: ProductVariationT[]) => void;
}) {
  const { items, unitPrice, variantAttrs, onChange } = props;
  const [itemsState, setItemsState] = useState<Array<ProductVariationT>>(items);
  useEffect(() => {
    setItemsState(items);
  }, [items]);

  const [indexToEdit, setIndexToEdit] = useState<number | null>(null);
  const { flg: modal, on: showModal, off: hideModal } = useFlag();
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();

  const [fields, setFields] = useState<ProductVariationT>();
  const updateFields = (newVal: ProductVariationT) =>
    setFields((prev) => ({ ...prev, ...newVal }));

  const _onChangePrice = (price: string) => updateFields({ price });
  const _onChangeStatus = (status: 0 | 1) =>
    updateFields({ is_active: status });
  const _onChangeSaleCount = (binary: 0 | 1) =>
    updateFields({ is_binary: binary });
  const _onChangeAttributes = (input: Array<{ id?: number; title?: string }>) =>
    updateFields({ attributes: input });

  const resetForm = () => {
    setIndexToEdit(null);
    setFields({});
  };

  const _onAdd = () => {
    const existAttrs = itemsState?.map((itemState) => itemState?.attributes);
    const existAttrsId = existAttrs?.map((attrs) =>
      attrs?.map((attr) => attr?.id)
    );

    if (fields) {
      const { attributes } = fields;
      const groupedAttrs: Array<Array<any>> = Object.values(
        arrayGroupBy(attributes, "parent_id")
      );

      const tmpAttrs =
        groupedAttrs.length === 1
          ? groupedAttrs?.[0]?.map((e: any) => [e])
          : cartesian(groupedAttrs);

      let tmpItems: Array<ProductVariationT> = [];

      for (const attrs of tmpAttrs) {
        const newAttrId = attrs.map((attr: ProductVariationItemT) => attr.id);

        const isExist = existAttrsId.findIndex(
          (e) => e && isEqualArrays(e, newAttrId)
        );

        if (isExist > -1) {
          continue;
        }

        const totalPriceRatio = attrs?.reduce(
          (sum: number, a: ProductVariationItemT) =>
            a.price_ratio ? a.price_ratio + sum : sum,
          0
        );

        const itemPrice =
          unitPrice && totalPriceRatio > 0
            ? totalPriceRatio * +unitPrice
            : unitPrice;

        tmpItems.push({
          id: null,
          price: itemPrice?.toString(),
          is_active: 1,
          is_binary: 0,
          attributes: attrs,
          total_price_ratio: totalPriceRatio,
        });
      }
      onChange([...items, ...tmpItems]);
    }
  };

  const _onEdit = () => {
    if (fields) {
      const tmp =
        typeof indexToEdit === "number" &&
        changeArrayAt(itemsState, indexToEdit, fields);
      tmp && onChange(tmp);
    }
  };

  const _onDelete = (close: () => void) => {
    const tmp =
      typeof indexToEdit === "number" && changeArrayAt(itemsState, indexToEdit);
    tmp && onChange(tmp);
    resetForm();
    close();
  };

  const _onConfirm = () => {
    if (indexToEdit && fields?.price === "") {
      Toast.warning({ message: strings.fill_all_fields });
      return;
    }

    if (
      indexToEdit &&
      fields?.price != 0 &&
      !patterns.number.test(fields?.price?.toString())
    ) {
      Toast.warning({ message: strings.invalid_price });
      return;
    }

    if (!indexToEdit) {
      if (variantAttrs?.length === 0) {
        Toast.warning({ message: strings.varied_product_warning });
        return;
      }
      if (!fields?.attributes) {
        Toast.warning({ message: strings.fill_all_fields });
        return;
      }
    }

    indexToEdit !== null ? _onEdit() : _onAdd();
    resetForm();
    hideModal();
  };

  const _onCancel = () => {
    resetForm();
    hideModal();
  };

  return (
    <>
      <View style={css.responsiveListContainer}>
        {itemsState?.length > 0 ? (
          itemsState?.map((item: ProductVariationT, key: number) => {
            return (
              <View key={String(key)} style={css.responsiveBoxContainer}>
                <GroupInfoBox
                  key={String(key)}
                  onEdit={() => {
                    setIndexToEdit(key);
                    setFields(item);
                    showModal();
                  }}
                  onDelete={() => {
                    setIndexToEdit(key);
                    showAlert();
                  }}
                  title={item.attributes
                    ?.map((attr) => attr?.title)
                    .join(" , ")}
                  icon={{ name: "chevron-left" }}
                  renderContent={() => (
                    <View style={{ ...css.rRowCenterAlign }}>
                      <Text weight="medium">{priceFormatter(item?.price)}</Text>
                      <Text size={"sm"} color={colors.textColorGray}>
                        {" "}
                        {strings.currency}
                      </Text>
                      <View style={css.container} />
                      {item.is_active === 0 && (
                        <View>
                          <Text size={"xs"} weight="bold" color={colors.danger}>
                            {strings.inactive}
                          </Text>
                        </View>
                      )}
                      {item?.is_binary === 1 && (
                        <View style={{ marginRight: 15 }}>
                          <Text
                            size={"xs"}
                            weight="bold"
                            color={colors.brightGreek}
                          >
                            {strings.binary}
                          </Text>
                        </View>
                      )}
                    </View>
                  )}
                />
              </View>
            );
          })
        ) : (
          <Text
            size="sm"
            color={colors.textColorGray}
            style={{ marginBottom: sizes.space / 2 }}
          >
            {strings.no_variant}
          </Text>
        )}
      </View>

      <AddItemButton onPress={showModal} />

      <EditModal
        visible={modal}
        onCancel={_onCancel}
        onConfirm={_onConfirm}
        renderForm={() => {
          return (
            <>
              {indexToEdit !== null && (
                <>
                  <InputLabel
                    label={`${strings.price} (${strings.currency})`}
                  />

                  <Input
                    keyboardType="number-pad"
                    value={fields?.price?.toString()}
                    onChangeText={_onChangePrice}
                    icon={{ name: "tag" }}
                    placeholder={strings.insert_price}
                    enablePriceFormatter
                  />

                  <View
                    style={{
                      marginBottom: sizes.space * 2,
                      ...css.rRowCenterAlign,
                    }}
                  >
                    <Switch
                      value={fields?.is_active}
                      onChange={_onChangeStatus}
                      activeLabel={strings.active}
                      inactiveLabel={strings.active}
                    />

                    <View style={{ width: 20 }} />

                    <Switch
                      value={fields?.is_binary}
                      onChange={_onChangeSaleCount}
                      activeLabel={strings.binary}
                      inactiveLabel={strings.binary}
                    />
                  </View>
                </>
              )}
              {indexToEdit === null &&
                variantAttrs &&
                (variantAttrs?.length > 0 ? (
                  <MultiSelectableItem
                    items={variantAttrs}
                    selectedItems={fields?.attributes}
                    onSelect={(itemIndex, item, itemList) => {
                      let tmp = fields?.attributes || [];
                      if (itemIndex > -1) {
                        tmp = changeArrayAt(tmp, itemIndex);
                      } else {
                        tmp?.push({ ...item, parent_id: itemList.id });
                      }
                      _onChangeAttributes(tmp);
                    }}
                  />
                ) : (
                  <View
                    style={{
                      borderWidth: 1,
                      margin: sizes.space,
                      padding: sizes.space,
                      borderColor: colors.border,
                      borderRadius: sizes.radius.md,
                    }}
                  >
                    <Text>{strings.no_attr_for_pick}</Text>
                  </View>
                ))}
            </>
          );
        }}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            description={strings.delete_product_variation_description}
            removeLoading={false}
            remove={() => _onDelete(close)}
            cancel={close}
          />
        )}
      />
    </>
  );
}
